import { useMemo, useState } from "react";
import { USERS_TABLE_DENSITY_KEY } from "@/core/constants/keys";
import { useTableSettings, useToast } from "@/ds";
import type { HubTableDataTableProps } from "@/ds";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useRoleOptions } from "@/screens/control-panel/hooks/use-role-options";
import type { User } from "@/modules/users";
import { useTableSettingsAppliedToast } from "@/shared/hooks";
import { CONTROL_PANEL_MOBILE_QUERY } from "../control-panel.constants";
import { useUsersExportacao } from "./hooks/use-users-exportacao";
import { useUsersFiltrosColunas } from "./hooks/use-users-filtros-colunas";
import { useUsersLista } from "./hooks/use-users-lista";
import { useUsersPerfil } from "./hooks/use-users-perfil";
import { useUsersStatus } from "./hooks/use-users-status";
import {
  USERS_ALL_COLUMNS,
  USERS_MAIN_COLUMNS,
  USERS_TABLE_LABEL,
  USERS_TABLE_MIN_WIDTH_REM,
} from "./users.constants";
import type { UsersColumnKey } from "./users.constants";
import { buildUserLinha, buildUsersColunas } from "./users.utils";
import type { UsersViewModel } from "./types-users";

/** Aba Usuários do Painel (HU 720, 721, 723, 1558 e a exportação da 1442): lista, edição de perfil e status. */
export function useUsers(): UsersViewModel {
  const isMobile = useMediaQuery(CONTROL_PANEL_MOBILE_QUERY);
  const toast = useToast();
  const [editando, setEditando] = useState<User | null>(null);
  const [alternando, setAlternando] = useState<User | null>(null);

  const settings = useTableSettings<UsersColumnKey>({
    densityKey: USERS_TABLE_DENSITY_KEY,
    allColumns: USERS_ALL_COLUMNS,
    mainColumns: USERS_MAIN_COLUMNS,
  });
  const perfis = useRoleOptions();
  const filterColumns = useUsersFiltrosColunas(perfis);
  const lista = useUsersLista();
  const notifySettingsApplied = useTableSettingsAppliedToast(toast);
  const exportView = useUsersExportacao({
    colunas: settings.visibleColumns,
    search: lista.busca,
    filters: lista.filters,
    filterValue: lista.filterValue,
    sort: lista.appliedSort,
    filterColumns,
    isEmpty: lista.status === "success" && lista.total === 0,
    toast,
  });
  const atualizarPerfil = useUsersPerfil({
    toast,
    onUpdated: () => {
      setEditando(null);
      lista.reload();
    },
  });
  const alterarStatus = useUsersStatus({
    toast,
    onUpdated: () => {
      setAlternando(null);
      lista.reload();
    },
    onDismissed: () => setAlternando(null),
  });

  const colunas = useMemo(() => buildUsersColunas(lista.totalSemPerfil), [lista.totalSemPerfil]);
  const linhas = useMemo(() => lista.items.map(buildUserLinha), [lista.items]);

  // O corpo rola dentro do cartão; abaixo da soma das larguras do Figma a tabela rola na horizontal.
  const dataTableProps: HubTableDataTableProps = {
    scrollable: true,
    scrollHeight: "flex",
    tableStyle: { minWidth: `${USERS_TABLE_MIN_WIDTH_REM}rem` },
    "aria-label": USERS_TABLE_LABEL,
  };

  return {
    isMobile,
    search: lista.search,
    buscar: lista.buscar,
    filterColumns,
    filterValue: lista.filterValue,
    applyFilters: lista.applyFilters,
    settings,
    exportView,
    notifySettingsApplied,
    colunas,
    items: linhas,
    total: lista.total,
    isFirstLoad: lista.status === "loading" && linhas.length === 0,
    status: lista.status,
    errorMessage: lista.errorMessage,
    reload: lista.reload,
    table: lista.tabela,
    dataTableProps,
    hasSearch: lista.hasSearch,
    hasFilters: lista.hasFilters,
    onClear: lista.hasSearch
      ? lista.clearSearch
      : lista.hasFilters
        ? lista.clearFilters
        : undefined,
    perfis,
    editando,
    editar: (linha) => setEditando(linha.user),
    fecharEdicao: () => setEditando(null),
    salvarEdicao: atualizarPerfil,
    alternando,
    alternar: (linha) => setAlternando(linha.user),
    fecharAlternancia: () => setAlternando(null),
    confirmarAlternancia: alterarStatus,
  };
}
